import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { find, get, isEmpty, isString, map } from 'lodash';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

import ChartComponent from '../ChartComponent';

const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'numeric-donut-pie-chart';

class NumericDonutPieChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      borderAlpha,
      borderColor,
      colorRange,
      dataNamed,
      dataNumeric,
      globalProperties,
      height,
      isCard,
      selectedElements,
      weight,
      width
    } = this.props;

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height])
      .style('overflow', 'visible');

    const data = map(dataNamed, (namedValue, index) => ({
      name: namedValue,
      value: Number(dataNumeric[index])
    }));

    const radius = Math.min(width, height) / 2;

    const arc = d3
      .arc()
      .innerRadius(radius * 0.67)
      .outerRadius(radius - 1);

    const pie = d3
      .pie()
      .padAngle(0.005)
      .sortValues((a, b) => b - a)
      .value(d => d.value);

    const arcs = pie(data);

    const donutPieSlices = svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('path')
      .data(arcs)
      .join('path')
      .attr('class', `${this.id}`)
      .attr('fill', (d, i) => (isString(colorRange) ? colorRange : colorRange[i]))
      .attr('fill-opacity', alpha)
      .attr('stroke', borderColor)
      .attr('stroke-opacity', borderAlpha)
      .attr('stroke-width', weight)
      .attr('d', arc)
      .attr('transform', `translate(${width / 2},${height / 2})`);

    if (!isCard) {
      donutPieSlices.append('title').text(d => `${d.data.name}: ${d.data.value}`);
    } else {
      svg
        .append('text')
        .attr('class', `${this.id}`)
        .attr('font-family', 'sans-serif')
        .attr('font-size', 16)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')
        .text('N');
    }

    if (!isCard && selectedElements.Text) {
      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('font-family', 'sans-serif')
        .attr('font-size', 12)
        .attr('text-anchor', 'middle')
        .selectAll('text')
        .data(arcs)
        .join('text')
        .attr(
          'transform',
          d =>
            `translate(${arc.centroid(d)[0] + width / 2}, ${arc.centroid(d)[1] +
              height / 2}) rotate(${
              (d.startAngle + d.endAngle) / 2 < Math.PI
                ? ((d.startAngle / 2 + d.endAngle / 2) * 180) / Math.PI - 90
                : ((d.startAngle / 2 + d.endAngle / 2 + Math.PI) * 180) / Math.PI - 90
            })`
        )
        .call(text =>
          text
            .append('tspan')
            .attr('y', '0.4em')
            .attr('font-weight', 'bold')
            .text(d => d.data.name)
        );
      svg
        .selectAll('text')
        .attr(
          'font-size',
          get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
        );
    }
  };
}

NumericDonutPieChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [3, 2, 1],
  colorRange: ['rgb(104,64,146)', 'rgb(159,71,146)', 'rgb(203,86,139)'],
  alpha: 0.9,
  weight: 2,
  borderColor: 'white',
  borderAlpha: 0.9,
  globalProperties: [],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  isCard: false
};

NumericDonutPieChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNamed: PropTypes.array,
  dataNumeric: PropTypes.array,
  colorRange: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  alpha: PropTypes.number,
  weight: PropTypes.number,
  borderColor: PropTypes.string,
  borderAlpha: PropTypes.number,
  globalProperties: PropTypes.array,
  selectedElements: PropTypes.object,
  isCard: PropTypes.bool
};

export default NumericDonutPieChart;
